import React, { FC, useMemo } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import SideBarImageLogo from 'components/common/SideBarImageLogo';
import { getSidebarBrand } from 'utils/companyBrand';

interface SidebarHeaderProps {
  isCollapsed: boolean;
  variant: 'temporary' | 'permanent';
  onToggleCollapsed: () => void;
  onClose?: () => void;
}

const SidebarHeader: FC<SidebarHeaderProps> = ({ isCollapsed, variant, onToggleCollapsed, onClose }) => {
  const sidebarBrand = useMemo(getSidebarBrand, []);
  const isLeadnamics = sidebarBrand?.label === 'Leadnamics';

  if (isCollapsed) {
    return (
      <Box
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 64,
          px: 1,
          py: 1.5,
          gap: 0.5,
        }}
      >
        <SideBarImageLogo
          alt="logo"
          sidebarBrand={sidebarBrand}
          style={{ width: '40px', height: '40px', objectFit: 'contain', flexShrink: 0 }}
        />
        <Tooltip
          title="Expandir menú lateral"
          placement="right"
          arrow
          slotProps={{ tooltip: { sx: { fontSize: '0.75rem' } } }}
        >
          <IconButton
            onClick={onToggleCollapsed}
            aria-label="Expandir menú lateral"
            size="small"
            disableRipple
            sx={{ color: 'grey.700', width: 32, height: 32 }}
          >
            <KeyboardDoubleArrowRightIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        height: 'max-content',
        px: 1.5,
        py: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <SideBarImageLogo
        alt="logo"
        sidebarBrand={sidebarBrand}
        style={{ width: '40px', height: '40px', objectFit: 'contain', flexShrink: 0 }}
      />
      {sidebarBrand && (
        <Typography
          variant="h2"
          component="span"
          sx={(theme) => ({
            color: isLeadnamics ? theme.palette.grey[900] : theme.palette.text.primary,
            fontSize: '1.3rem',
            fontWeight: 700,
            lineHeight: 1.2,
            minWidth: 0,
          })}
        >
          {sidebarBrand.label}
        </Typography>
      )}
      <Box sx={{ ml: 'auto', flexShrink: 0, display: 'flex', alignItems: 'center' }}>
        {variant === 'temporary' ? (
          <IconButton
            onClick={onClose}
            aria-label="Cerrar menú"
            disableRipple
            sx={{ color: 'grey.700', width: 40, height: 40 }}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
            <Tooltip
              title="Colapsar menú lateral"
              placement="right"
              arrow
              slotProps={{ tooltip: { sx: { fontSize: '0.75rem' } } }}
            >
              <IconButton
                onClick={onToggleCollapsed}
                aria-label="Colapsar menú lateral"
                size="small"
                disableRipple
                sx={{ color: 'grey.700', width: 40, height: 40, padding: 0.5 }}
              >
                <KeyboardDoubleArrowLeftIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default React.memo(SidebarHeader);
